import ExcelJS from 'exceljs';
import { prettifyDateTimeHHmm } from 'odfjell-date-time-utils/dateTimeFormatter';
import { InputDataTypeEnum } from 'src/enums/inputDataTypeEnum';
import { DropdownTypeEnum } from 'src/enums/dropdownTypeEnum';
import { setCellValueWithFont } from '../reportEventExcelUtils';
import { TankersEvent } from 'src/types/ViewModel/TankersEvent';
import { EventTypeLevel } from 'src/types/ViewModel/EventTypeLevel';
import { EventTypeInputReg } from 'src/types/ViewModel/EventTypeInputReg';
import { DropdownGroupReg } from 'src/types/ViewModel/DropdownGroupReg';

export const getEventTypeName = (ev: TankersEvent, evTypeLevelList: EventTypeLevel[]): string => {
    const evTypeList = evTypeLevelList.flatMap((o) => o.eventTypeGroupArray).flatMap((o) => o.eventTypeArray);
    const evType = evTypeList.find((o) => o.eventTypeId === ev.eventTypeId);
    return evType?.name || '<Not found>';
};

export const addEventRow = (
    worksheet: ExcelJS.Worksheet,
    ev: TankersEvent,
    rowNrFrom: number,
    eventTypeName: string,
    evTypeLevelList: EventTypeLevel[]
): void => {
    const { comment, noOfLines } = getCellValueComment(ev, evTypeLevelList);

    // Set row height based on number of lines
    const row = worksheet.getRow(rowNrFrom);
    row.height = (row.height || 15) * noOfLines;

    if (ev.eventDate?.actualDateTimeLocal) {
        setCellValueWithFont(worksheet, rowNrFrom, 1, prettifyDateTimeHHmm(ev.eventDate.actualDateTimeLocal));
    }
    setCellValueWithFont(worksheet, rowNrFrom, 2, eventTypeName, { wrapText: true });
    setCellValueWithFont(worksheet, rowNrFrom, 3, comment, { wrapText: true, mergeAcross: 2, align: 'left' });
};

export const getCellValueComment = (ev: TankersEvent, evTypeLevelList: EventTypeLevel[]): { comment: string; noOfLines: number } => {
    let { comment, noOfLines } = getEventComment(ev, evTypeLevelList);

    const evInpList =
        ev.eventTypeInputRegList?.filter(
            (o) =>
                o.inputDataTypeId === InputDataTypeEnum.Dropdown &&
                o.dropdownGroupReg !== null &&
                o.dropdownGroupReg!.dropdownTypeId !== DropdownTypeEnum.ParentEventType
        ) || [];

    const nrOfDropdowns = getNumberOfDropdowns(evInpList);
    if (comment && nrOfDropdowns > 0) {
        comment += addNewLines(1);
    }

    let x = 1;
    for (const evInp of evInpList) {
        if (evInp.dropdownGroupReg) {
            if (
                evInp.dropdownGroupReg.dropdownTypeId === DropdownTypeEnum.CargoUnloadingOperations ||
                evInp.dropdownGroupReg.dropdownTypeId === DropdownTypeEnum.CargoLoadingOperations ||
                evInp.dropdownGroupReg.dropdownTypeId === DropdownTypeEnum.CargoLoadingAndUnloadingOperations
            ) {
                comment += getDropdownHeading(evInp, evTypeLevelList) + addNewLines(1);
                noOfLines++;
                const { cmt, noOfL } = getDropdownValuesMultipleLines(evInp.dropdownGroupReg);
                comment += cmt;
                noOfLines += noOfL;
            } else {
                const { cmt, noOfL } = getDropdownValuesOneLine(getDropdownHeading(evInp, evTypeLevelList), evInp.dropdownGroupReg);
                comment += cmt;
                noOfLines += noOfL;
            }
            if (x++ < nrOfDropdowns) {
                comment += addNewLines(1);
            }
        }
    }

    return { comment, noOfLines };
};

export const getEventComment = (ev: TankersEvent, evTypeLevelList: EventTypeLevel[]): { comment: string; noOfLines: number } => {
    let noOfLines = 0;
    const evTypeList = evTypeLevelList.flatMap((o) => o.eventTypeGroupArray).flatMap((o) => o.eventTypeArray);
    const evType = evTypeList.find((o) => o.eventTypeId === ev.eventTypeId);

    if (evType) {
        const evTypeInp = evType.eventTypeInputArray?.find((o) => o.inputDataTypeId === InputDataTypeEnum.String && o.inputHeading === 'Comments');
        if (evTypeInp) {
            const inpReg = ev.eventTypeInputRegList?.find((o) => o.eventTypeInputId === evTypeInp.eventTypeInputId);
            if (inpReg?.stringValueReg) {
                // Clean up multiple spaces and tabs
                let cleanedValue = inpReg.stringValueReg.replace(/\t/g, ' ').replace(/[ ]{2,}/g, ' ');

                const inpStrList = cleanedValue.split('\n');
                inpStrList.forEach((line) => {
                    const noOfL = getNumberOfLines(line);
                    noOfLines += noOfL;
                });

                cleanedValue = cleanedValue.replace(/\n/g, '\r\n').replace(/[ ]{2,}/g, ' ');
                return { comment: cleanedValue, noOfLines: Math.max(noOfLines, 0) };
            }
        }
    }

    return { comment: '', noOfLines: 0 };
};

export const getNumberOfLines = (str: string): number => {
    if (!str) return 0;
    const x = Math.floor((str.length - 1) / 43);
    return x + 1;
};

export const getNumberOfDropdowns = (evTypeInpRegList: EventTypeInputReg[]): number => {
    if (!evTypeInpRegList || evTypeInpRegList.length === 0) return 0;

    const dropdownList = evTypeInpRegList.filter((o) => o.inputDataTypeId === InputDataTypeEnum.Dropdown && o.dropdownGroupReg !== null);
    return dropdownList.length;
};

export const addNewLines = (nr: number): string => '\n'.repeat(nr);

export const getDropdownHeading = (evTypeInpReg: EventTypeInputReg, evTypeLevelList: EventTypeLevel[]): string => {
    const evTypeInpList = evTypeLevelList
        .flatMap((o) => o.eventTypeGroupArray)
        .flatMap((o) => o.eventTypeArray)
        .flatMap((o) => o.eventTypeInputArray || []);

    const evTypeInp = evTypeInpList.find((o) => o.eventTypeInputId === evTypeInpReg.eventTypeInputId);
    if (evTypeInp) {
        return evTypeInp.inputHeading + ': ';
    } else if (evTypeInpReg.dropdownGroupReg?.dropdownTypeId === DropdownTypeEnum.ParentEventType) {
        return 'Parent: ';
    }
    return 'Unknown: ';
};

export const getDropdownValuesMultipleLines = (drGroupReg: DropdownGroupReg): { cmt: string; noOfL: number } => {
    let str = '';
    let y = 1;
    let noOfLines = 0;

    for (const drItem of drGroupReg.dropdownItemRegList) {
        const itemStr = '   -' + drItem.dropdownItemValue;
        const x = getNumberOfLines(itemStr);
        str += itemStr;
        noOfLines += x;
        if (y++ < drGroupReg.dropdownItemRegList.length) {
            str += addNewLines(1);
        }
    }

    return { cmt: str, noOfL: noOfLines };
};

export const getDropdownValuesOneLine = (heading: string, drGroupReg: DropdownGroupReg): { cmt: string; noOfL: number } => {
    let str = heading;
    let y = 1;

    for (const drItem of drGroupReg.dropdownItemRegList) {
        str += drItem.dropdownItemValue;
        if (y++ < drGroupReg.dropdownItemRegList.length) {
            str += ', ';
        }
    }

    const noOfLines = getNumberOfLines(str);
    return { cmt: str, noOfL: noOfLines };
};

export const addEventLines = (worksheet: ExcelJS.Worksheet, evList: TankersEvent[], evTypeLevelList: EventTypeLevel[], nrOfRows: number): void => {
    let rowNr = nrOfRows + 1;

    if (evList.length > 0) {
        // Insert rows for the events
        for (let i = 0; i < evList.length; i++) {
            worksheet.insertRow(rowNr + i, []);
        }

        // Sort events by actual date time and add them to worksheet
        const sortedEvents = evList
            .filter((ev) => ev.eventDate?.actualDateTimeLocal)
            .sort((a, b) => {
                const dateA = a.eventDate?.actualDateTimeLocal?.getTime() || 0;
                const dateB = b.eventDate?.actualDateTimeLocal?.getTime() || 0;
                return dateA - dateB;
            });

        for (const ev of sortedEvents) {
            let evTypeName = getEventTypeName(ev, evTypeLevelList);
            if (ev.dependencyDisplayName) {
                evTypeName += ' ' + ev.dependencyDisplayName;
            }
            addEventRow(worksheet, ev, rowNr++, evTypeName, evTypeLevelList);
        }
    }
};
